<template>
  <div>
    <label
      v-if="(label || hasLabel) && !floatingLabel"
      :for="id || internalId"
      :class="
        cn(
          'text-100 mb-2.5 mr-1.5 flex items-baseline text-sm font-light capitalize',
          labelClass,
        )
      "
    >
      {{ label }}
    </label>
    <div class="relative">
      <select
        :id="id || internalId"
        v-model="internalValue"
        :name="name"
        class="peer h-12 w-full cursor-pointer rounded-none border border-gray-30 bg-white px-2.5 text-base leading-[normal] text-primary"
        :class="[floatingLabel && internalValue ? 'pb-1.5 pt-5.5' : 'py-3.5']"
        :data-e2e-placeholder="dataE2ePlaceholder"
        :data-e2e-item="dataE2eItemGroup"
      >
        <option
          v-if="placeholder"
          value=""
          :selected="!internalValue"
          :disabled="!unselectable"
        >
          {{ placeholder }}
        </option>
        <template v-if="isMounted && titleKey && valueKey">
          <option
            v-for="(option, idx) in items"
            :key="valueKey ? option[valueKey] : idx"
            :value="option[valueKey]"
            :selected="internalValue === option[valueKey]"
            :disabled="option.disabled"
            :data-e2e-item="
              showDataE2eItem
                ? sizeDataE2eItem
                  ? `size-filter-${viewType}`
                  : `${option.sku}-filter-${viewType}`
                : null
            "
          >
            <slot name="option" :option="option">{{ option[titleKey] }}</slot>
          </option>
        </template>
      </select>
      <label
        v-if="floatingLabel && (label || hasLabel)"
        :for="id || internalId"
        :class="[
          internalValue ? 'floating-label' : '',
          cn(
            'pointer-events-none absolute left-2.5 top-0 h-full w-full origin-[0_0] items-center truncate py-4 pr-10 text-sm leading-none text-gray-60 transition duration-100 ease-in-out',
          ),
        ]"
      >
        <slot name="label">
          <span>{{ label }}</span>
        </slot>
      </label>
    </div>
    <SharedFieldHint
      v-if="!hideDetails"
      :error="Boolean(errorMessage)"
      :message="errorMessage || hint"
    />
  </div>
</template>

<script setup lang="ts" generic="T extends Record<string, any>">
import { useField } from "vee-validate";

const props = withDefaults(
  defineProps<{
    id?: string;
    modelValue: any;
    items: T[];
    titleKey?: string;
    valueKey?: string;
    placeholder?: string;
    name: string;
    hideDetails?: boolean;
    hint?: string;
    lazy?: boolean;
    unselectable?: boolean;
    floatingLabel?: boolean;
    label?: string;
    labelClass?: string;
    dataE2ePlaceholder?: string;
    dataE2eItemGroup?: string;
    showDataE2eItem?: boolean;
    sizeDataE2eItem?: boolean;
    viewType?: string;
  }>(),
  {
    titleKey: "label",
    valueKey: "value",
    lazy: true,
    labelClass: "",
  },
);

const emit = defineEmits<{
  (e: "update:modelValue", selectedValue: string): void;
}>();

const isMounted = useMounted();

const slots = useSlots();

const internalId = useId();

const { errorMessage, handleChange, meta } = useField(props.name, undefined, {
  initialValue: props.modelValue,
});

const hasLabel = computed(() => {
  return !!slots.label;
});

const internalValue = computed({
  get() {
    return props.modelValue || "";
  },
  set(newValue) {
    if (props.name) {
      // handleChange(props.modelValue);
      handleChange(newValue);
    }
    emit("update:modelValue", newValue);
  },
});

defineExpose({
  meta,
  value: props.modelValue,
  name: props.name,
});
</script>

<style>
.floating-label {
  @apply -translate-y-1 scale-75 peer-disabled:pointer-events-none peer-disabled:opacity-50;
}

select,
option.selected {
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}
</style>
